import hotkeys from 'hotkeys-js';
import { useEffect } from 'react';
import { SHORTCUT } from '../components/admin/curator/CuratorShortcuts/curatorShortcuts.config';
import { useUnityContext } from '../components/container/unityContainer';
import { getShortcut, hotkeysFilter } from '../helpers/shortcutsHelper';
import { KEYBOARD_EVENT } from '../helpers/unityPubSub';
import { getOsInfo } from './useDetectOS';
import { isConfiguratorView, isRetailAutomation } from '../components/Configurator/utils';
import { useSelector } from "react-redux";
import { curatorSelector } from "../redux/slicers/admin/curatorSlicer";
// import { getAppState } from '../redux/store';
// import { selectInfoWarningPopupMessage, selectShowInfoWarningPopup } from '../redux/slicers/admin/curatorUnityObjectSlicer';
// import { selectedObjectPasteCondition } from '../components/admin/curator/common/helper'

hotkeys.filter = hotkeysFilter;

export const useSetupShortcuts = (
  {
    shortcuts,
    shortcutsConfig,
    otherShortcuts,
    enableMultiSelectEvents = true,
    areShortcutsEnabled = () => true,
  },
  deps
) => {
  const unityContext = useUnityContext();
  const sidebarActiveSection = useSelector(
    (state) => curatorSelector(state).sidebarSection
  );

  useEffect(() => {
    if (!areShortcutsEnabled()) return;

    Object.entries(shortcuts).forEach(([name, callback]) => {
      if (typeof callback === 'function') {
        if(name === SHORTCUT.DESELECT_ALL && sidebarActiveSection) return null
        hotkeys(getShortcut(shortcutsConfig, name), (e, handler) => {
          e.preventDefault();
          callback(e, handler);
        });
      } else {
        const { keyUp, keyDown } = callback;
        hotkeys(getShortcut(shortcutsConfig, name), { keyup: true }, (e, handler) => {
          if ((e.type === 'keydown', name)) {
            keyDown(e, handler);
          }

          if (e.type === 'keyup') {
            keyUp(e, handler);
          }
        });
      }
    });

    if (otherShortcuts) {
      Object.entries(otherShortcuts).forEach(([eventName, callback]) => {
        if (eventName === KEYBOARD_EVENT.ESC_PRESSED) {
          hotkeys('esc', (e, handler) => {
            callback(e, handler);
          });
        }
      });
    }

    if (enableMultiSelectEvents) {
      hotkeys('*', { keyup: true }, function (e) {
        // handle shortkeys with mouse combinations
        unityContext.shortcutsModule.removeSelection(false);
        unityContext.shortcutsModule.addSelection(false);
        if (!isConfiguratorView() && !isRetailAutomation()) {
          unityContext.shortcutsModule.SingleObjectSelectionReceiver(false);
        }
        // unityShortcuts.snapObject(false);

        window.hotkeys = hotkeys;
        const CAPS_LOCK = 20;

        let pressedKeys = hotkeys.getPressedKeyCodes();

        if (pressedKeys.includes(CAPS_LOCK)) {
          // related to https://app.clickup.com/t/3326989/DEV-12656
          pressedKeys = pressedKeys.filter((key) => key !== CAPS_LOCK);
        }

        const onlyOnePressed = pressedKeys.length === 1;
        const onlyTwoPressed = pressedKeys.length === 2;
        const { altKey, ctrlKey, metaKey, shiftKey } = hotkeys.modifierMap;
        const ctrlKeyList = ['Meta', 'Ctrl'];
        const ctrlOrCommand =
          pressedKeys.includes(ctrlKey) ||
          pressedKeys.includes(metaKey) ||
          (!getOsInfo().isMac && ctrlKeyList.includes(e.key)); // https://app.clickup.com/t/3326989/DEV-12934
        const altPressed = pressedKeys.includes(altKey) || pressedKeys.includes('Alt');
        const shiftPressed = pressedKeys.includes(shiftKey) || pressedKeys.includes('Shift');

        // console.log('DEBUG_HOTKEYS_ONLY', altPressed, pressedKeys)

        // console.log('SingleObjectSelectionReceiver', pressedKeys, e.type, { ctrlOrCommand, ctrlKey, metaKey, 'e.key': e.key  })

        const deselectKeys = shortcutsConfig[SHORTCUT.DESELECT].keys;
        const selectMultipleKeys = shortcutsConfig[SHORTCUT.SELECT_MULTIPLE].keys;
        const selectChildKeys = shortcutsConfig[SHORTCUT.SELECT_CHILD].keys;
        const deselectChildKeys = shortcutsConfig[SHORTCUT.DESELECT_CHILD].keys;
        const selectMultipleChildKeys = shortcutsConfig[SHORTCUT.SELECT_MULTIPLE_CHILD].keys;
        // const showPopup = selectShowInfoWarningPopup(getAppState());
        // const popupMessage = selectInfoWarningPopupMessage(getAppState());

        const config = [
          {
            keys: deselectKeys,
            callback: () => unityContext.shortcutsModule.removeSelection(true),
          },
          {
            keys: selectMultipleKeys,
            callback: () => {
              // if (showPopup && popupMessage?.includes('alignment') && selectedObjectPasteCondition(true)) {
              //   unityContext.alignmentModule.assignReferenceObject();
              // } 
              unityContext.shortcutsModule.addSelection(true);
            }
          },
          {
            keys: selectChildKeys,
            callback: () => {
              if (!isConfiguratorView() && !isRetailAutomation()) {
                unityContext.shortcutsModule.SingleObjectSelectionReceiver(true);
              }
            },
          },
          {
            keys: deselectChildKeys,
            callback: () => {
              if (!isConfiguratorView() && !isRetailAutomation()) {
                unityContext.shortcutsModule.SingleObjectSelectionReceiver(true);
              }
              unityContext.shortcutsModule.removeSelection(true);
            },
          },
          {
            keys: selectMultipleChildKeys,
            callback: () => {
              if (!isConfiguratorView() && !isRetailAutomation()) {
                unityContext.shortcutsModule.SingleObjectSelectionReceiver(true);
              }
              unityContext.shortcutsModule.addSelection(true);
            },
          },
        ];

        config.forEach((c) => {
          const keys = c.keys.filter((k) => k.toLowerCase() !== 'click');
          const keysCountIsValid =
            (onlyOnePressed && keys.length === 1) || (onlyTwoPressed && keys.length === 2);

          // console.log('DEBUG_HOTKEYS', { keysCountIsValid })
          if (!keysCountIsValid) return;

          const sameKeys = keys.every((k) => {
            if (k === 'Shift') return shiftPressed; //  && e.key === 'Shift';
            if (k === 'Alt') return altPressed; // && e.key === 'Alt';
            if (k === 'Ctrl') return ctrlOrCommand; // && ctrlKeyList.includes(e.key);
            return e.key.toLowerCase() === k.toLowerCase();
          });

          // console.log('DEBUG_HOTKEYS', { sameKeys, keys, altPressed, ctrlOrCommand })
          if (!sameKeys) return;

          c.callback();
        });
      });
    }

    return () => {
      Object.keys(shortcuts).forEach((name) => {
        hotkeys.unbind(getShortcut(shortcutsConfig, name));
      });

      hotkeys.unbind('esc');

      if (enableMultiSelectEvents) {
        hotkeys.unbind('*');
      }
    };
  }, deps);
};
